/**
 * The presentational blocked list. Every value is passed in so both the
 * populated and the empty state are testable with no network stub. The
 * unblock loading state is scoped to one row.
 */
import { Button } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { SectionEmptyState } from "../../shell/SectionScreen";
import { avatarPlateStyle } from "../profile/avatarPlateStyle";
import {
  BLOCKED_EMPTY_BODY,
  BLOCKED_EMPTY_HEADLINE,
  type typeBlockedRow,
  UNBLOCK_LABEL,
  UNBLOCK_PENDING_LABEL,
} from "./privacyScreenLogic";

export const BlockedList = ({
  rows,
  unblockingAccountId,
  onUnblock,
}: {
  rows: readonly typeBlockedRow[];
  /** The row whose unblock is in flight, or null. Scopes the loading state to one row. */
  unblockingAccountId: string | null;
  onUnblock: (row: typeBlockedRow) => void;
}): ReactNode => {
  if (rows.length === 0) {
    return (
      <div data-testid="blocked-empty">
        <SectionEmptyState
          headline={BLOCKED_EMPTY_HEADLINE}
          body={BLOCKED_EMPTY_BODY}
        />
      </div>
    );
  }

  return (
    <div
      data-testid="blocked-list"
      className="flex flex-col divide-y divide-line rounded-lg border border-line bg-surface"
    >
      {rows.map((row) => (
        <div
          key={row.accountId}
          data-testid={`blocked-row-${row.accountId}`}
          className="flex items-center gap-3 p-4"
        >
          {row.photoUrl !== null ? (
            <img
              src={row.photoUrl}
              alt=""
              className="h-10 w-10 shrink-0 rounded-full object-cover"
            />
          ) : (
            <span
              aria-hidden="true"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-caption"
              style={avatarPlateStyle(row.displayName)}
            >
              {row.initials}
            </span>
          )}
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-body-strong text-ink">
              {row.displayName}
            </span>
            {row.handle === "" ? null : (
              <span className="truncate text-caption text-ink-secondary">
                {row.handle}
              </span>
            )}
          </span>
          <Button
            variant="secondary"
            className="ml-auto"
            data-testid={`blocked-unblock-${row.accountId}`}
            loading={unblockingAccountId === row.accountId}
            loadingLabel={UNBLOCK_PENDING_LABEL}
            onClick={() => {
              onUnblock(row);
            }}
          >
            {UNBLOCK_LABEL}
          </Button>
        </div>
      ))}
    </div>
  );
};
